/**
 * A confirmed-but-not-yet-paid online order attempt, remembered per browser
 * tab so retrying the exact same order (back from the payment gateway, page
 * reload) reopens the existing payment session instead of calling
 * POST /receipt/confirm again. Confirming twice creates a second receipt for
 * the same order, which reaches the restaurant as a duplicate.
 *
 * sessionStorage is deliberate: it survives the redirect to the payment
 * gateway and back, but dies with the tab. All access is try/catch-guarded —
 * some webviews block storage entirely, in which case the guard is simply off.
 */

/**
 * Exported so tests assert against the real key rather than a literal that keeps
 * passing after a rename.
 */
export const STORAGE_KEY = "prepit-pending-online-payment";

/** Payment sessions go stale quickly; past this age a fresh confirm is issued. */
const MAX_AGE_MS = 10 * 60 * 1000;

type PendingOnlinePayment = {
  /** JSON of the confirm request body — same fingerprint means same order. */
  fingerprint: string;
  paymentLink: string;
  receiptId?: number;
  createdAtMs: number;
};

export const buildConfirmFingerprint = (requestBody: unknown): string => {
  try {
    return JSON.stringify(requestBody);
  } catch {
    return "";
  }
};

export const getReusablePendingOnlinePayment = (
  fingerprint: string
): PendingOnlinePayment | null => {
  if (!fingerprint) {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const stored = JSON.parse(raw) as PendingOnlinePayment;
    if (
      stored.fingerprint === fingerprint &&
      stored.paymentLink &&
      Date.now() - stored.createdAtMs < MAX_AGE_MS
    ) {
      return stored;
    }
  } catch {
    // Blocked/corrupted storage — behave as if nothing was stored.
  }

  return null;
};

export const storePendingOnlinePayment = (
  payment: Omit<PendingOnlinePayment, "createdAtMs">
): void => {
  if (!payment.fingerprint || !payment.paymentLink) {
    return;
  }
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...payment, createdAtMs: Date.now() })
    );
  } catch {
    // Blocked storage — the guard is simply off for this session.
  }
};

/**
 * Read the stored attempt without clearing it, whatever its fingerprint or age.
 *
 * Used when the customer lands back on the site without having paid: the record
 * exists only because we sent them to the gateway, so its presence on any page
 * that is not part of the payment flow means that trip did not go through.
 *
 * Reading without clearing is deliberate. The stamp is what stops a second
 * confirm creating a duplicate order, so it may only be dropped once the order
 * it points at is actually cancelled — see `clearPendingOnlinePayment`, which the
 * abandon caller invokes on success only.
 */
export const peekPendingOnlinePayment = (): PendingOnlinePayment | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }

    return JSON.parse(raw) as PendingOnlinePayment;
  } catch {
    // Blocked/corrupted storage — nothing to act on.
    return null;
  }
};

export const clearPendingOnlinePayment = (): void => {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do.
  }
};
